import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { BottomSheet } from '../../../../components/ui/BottomSheet';
import { LABEL_STANDARD } from '../../../../lib/formStyles';
import {
  CABLE_TYPE_OPTIONS,
  CABLE_SIZES,
  PHASE_OPTIONS,
  getMvPerAmPerM,
  getCableSizesForType,
} from './voltDropACCalcData';

const INPUT_BASE =
  'flex h-9 w-full items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';

function SelectTrigger({ label, placeholder, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${INPUT_BASE} text-left ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <span className={label ? '' : 'text-gray-500 dark:text-gray-400'}>
        {label || placeholder}
      </span>
      <svg
        className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
  );
}

function RadioList({ options, value, onChange, onSelect }) {
  return (
    <div className="space-y-1">
      {options.map((opt) => {
        const optVal = opt.value !== undefined && opt.value !== null ? opt.value : opt.id;
        const isChecked = value === optVal || (opt.id === '' && !value);
        return (
          <label
            key={opt.id || opt.label}
            className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
          >
            <input
              type="radio"
              name={String(opt.id)}
              value={optVal}
              checked={isChecked}
              onChange={() => {
                onChange(opt.id === '' ? '' : optVal);
                onSelect?.();
              }}
              className="h-4 w-4 text-blue-600"
            />
            <span className="text-gray-900 dark:text-gray-100">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

const initialState = {
  load: '',
  supplyVoltage: '',
  cableLength: '',
  cableType: '',
  cableSize: '',
  phase: '',
};

export default function VoltDropACCalc() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [form, setForm] = useState(initialState);
  const [result, setResult] = useState(null);
  const [openSheet, setOpenSheet] = useState(null);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';
  const cableSizePath = isMobile ? '/tenant/mobile/calculators/cable-size' : '/tenant/tools/calculators/cable-size';

  const updateForm = (field, value) => {
    setForm((p) => {
      const next = { ...p, [field]: value };
      if (field === 'cableType') {
        next.cableSize = '';
      }
      return next;
    });
  };

  const getLabel = (options, value, fallback = 'Select') => {
    if (!value) return fallback;
    const opt = options.find((o) => (o.value ?? o.id) === value);
    return opt?.label ?? fallback;
  };

  const cableSizesAvailable = getCableSizesForType(form.cableType);
  const cableSizeOptions = cableSizesAvailable.map((s) => ({
    id: String(s),
    label: `${s}mm`,
    value: String(s),
  }));

  const handleCalculate = () => {
    const Ib = parseFloat(form.load);
    const Uo = parseFloat(form.supplyVoltage) || 230;
    const L = parseFloat(form.cableLength);
    const mvAm = getMvPerAmPerM(form.cableType, form.cableSize, form.phase);

    if (!Ib || !L || !mvAm) {
      return;
    }

    const vdVolts = (mvAm * Ib * L) / 1000;
    const vdPercent = (vdVolts / Uo) * 100;

    setResult({
      voltDropVolts: vdVolts.toFixed(2),
      voltDropPercent: vdPercent.toFixed(2),
    });
  };

  const handleReset = () => {
    setForm(initialState);
    setResult(null);
  };

  const canCalculate =
    form.load &&
    form.supplyVoltage &&
    form.cableLength &&
    form.cableType &&
    form.cableSize &&
    form.phase;

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">AC Volt Drop Calculator</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      {/* Red info banner */}
      <div className="mt-4 px-4 py-3 rounded-lg bg-red-600 dark:bg-red-700 text-white text-sm">
        Voltage drop calculator for AC radial circuits
      </div>

      <div className="space-y-4 pb-6 mt-4">
        {/* Load */}
        <div>
          <label className={LABEL_STANDARD}>Load</label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
            <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
              A
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              placeholder="Amps"
              value={form.load}
              onChange={(e) => updateForm('load', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Supply Voltage */}
        <div>
          <label className={LABEL_STANDARD}>Supply Voltage</label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
            <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
              V
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="1"
              placeholder="Volts"
              value={form.supplyVoltage}
              onChange={(e) => updateForm('supplyVoltage', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Cable Length */}
        <div>
          <label className={LABEL_STANDARD}>Cable Length</label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
            <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
              L
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              placeholder="Meters"
              value={form.cableLength}
              onChange={(e) => updateForm('cableLength', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Cable Type */}
        <div>
          <label className={LABEL_STANDARD}>Cable Type</label>
          <SelectTrigger
            label={getLabel(CABLE_TYPE_OPTIONS, form.cableType)}
            placeholder="Select"
            onClick={() => setOpenSheet('cableType')}
          />
        </div>

        {/* Cable Size */}
        <div>
          <label className={LABEL_STANDARD}>Cable Size</label>
          <SelectTrigger
            label={form.cableSize ? `${form.cableSize}mm` : null}
            placeholder={form.cableType ? 'Select' : 'Select Cable Type'}
            onClick={() => form.cableType && setOpenSheet('cableSize')}
            disabled={!form.cableType}
          />
          {form.cableType === 'cu-flat-twin' && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Limit to max 16mm for Flat Twin & Earth
            </p>
          )}
        </div>

        {/* Phase */}
        <div>
          <label className={LABEL_STANDARD}>Phase</label>
          <SelectTrigger
            label={getLabel(PHASE_OPTIONS, form.phase)}
            placeholder="Select"
            onClick={() => setOpenSheet('phase')}
          />
        </div>

        {/* Results table */}
        <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 bg-green-600 dark:bg-green-700">
            <div className="px-4 py-3 text-sm font-semibold text-white">Parameter</div>
            <div className="px-4 py-3 text-sm font-semibold text-white text-right">Result</div>
          </div>
          <div className="bg-white dark:bg-gray-800">
            <div className="grid grid-cols-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Volt Drop (Volts)
              </span>
              <span className="text-sm text-gray-900 dark:text-gray-100 text-right">
                {result ? `${result.voltDropVolts} V` : '-'}
              </span>
            </div>
            <div className="grid grid-cols-2 px-4 py-3">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Volt Drop (%)
              </span>
              <span className="text-sm text-gray-900 dark:text-gray-100 text-right">
                {result ? `${result.voltDropPercent} %` : '-'}
              </span>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-600 dark:text-gray-400">
          Max volt drop: Lighting = 3%, Power = 5%
        </p>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-600 dark:text-gray-400">
          This volt drop calculator calculates the volt drop based on your selections
          and inputs, it does not verify you have selected a suitable cable size, use
          the{' '}
          <button
            type="button"
            onClick={() => navigate(cableSizePath)}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Cable Calculator
          </button>{' '}
          to calculate cable sizes.
        </p>
      </div>

      {/* Selection BottomSheets */}
      <BottomSheet
        open={openSheet === 'cableType'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        title="Select"
        maxHeight="max-h-[85vh]"
      >
        <RadioList
          options={CABLE_TYPE_OPTIONS}
          value={form.cableType}
          onChange={(v) => updateForm('cableType', v)}
          onSelect={() => setOpenSheet(null)}
        />
      </BottomSheet>
      <BottomSheet
        open={openSheet === 'cableSize'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        title="Select"
        maxHeight="max-h-[85vh]"
      >
        <RadioList
          options={[{ id: '', label: 'Select', value: '' }, ...cableSizeOptions]}
          value={form.cableSize}
          onChange={(v) => updateForm('cableSize', v)}
          onSelect={() => setOpenSheet(null)}
        />
      </BottomSheet>
      <BottomSheet
        open={openSheet === 'phase'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        title="Select"
      >
        <RadioList
          options={PHASE_OPTIONS}
          value={form.phase}
          onChange={(v) => updateForm('phase', v)}
          onSelect={() => setOpenSheet(null)}
        />
      </BottomSheet>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            AC Volt Drop Calculator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Calculate AC voltage drop for radial circuits
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
